// パックの版（エディション）と乗り換え価格。金額はサーバーがこの関数で決め、画面から送られた金額は使わない。

/** 版を持つパック（値段を決めるのに要る項目だけ） */
export type EditionPack = {
  id: string;
  /** coalesce(series_id, id)。同じ値なら同じシリーズ */
  seriesKey: string;
  editionNo: number;
  editionLabel: string;
  priceJpy: number;
  upgradePriceJpy: number | null;
  upgradeUntil: Date | null;
  /** 期間限定価格（null は設定なし） */
  salePriceJpy: number | null;
  saleStartsAt: Date | null;
  saleEndsAt: Date | null;
  saleLabel: string;
};

/** 主催者が持っているパック（entitlement の行） */
export type OwnedEdition = {
  entitlementId: string;
  packId: string;
  seriesKey: string;
  editionNo: number;
  editionLabel: string;
};

export type PriceQuote = {
  /** 通常価格 */
  listJpy: number;
  /** 実際の請求額 */
  amountJpy: number;
  discount: 'none' | 'upgrade' | 'campaign';
  /** 乗り換えの根拠になった前の版（乗り換え価格を使うときだけ） */
  from: { entitlementId: string; packId: string; editionLabel: string } | null;
  /** 使った割引の締め切り（乗り換え価格の締め切り、または期間限定価格の終了日時） */
  until: Date | null;
  /** いま期間中の期間限定価格（使わなかったときも、画面に出すために返す） */
  sale: { label: string; priceJpy: number; endsAt: Date } | null;
};

/** 期間限定価格が now の時点で期間中か（開始日時 ≦ now ＜ 終了日時） */
export function saleActive(pack: Pick<EditionPack, 'priceJpy' | 'salePriceJpy' | 'saleStartsAt' | 'saleEndsAt'>, now: Date) {
  return (
    pack.salePriceJpy !== null &&
    pack.salePriceJpy < pack.priceJpy &&
    !!pack.saleStartsAt &&
    !!pack.saleEndsAt &&
    pack.saleStartsAt.getTime() <= now.getTime() &&
    now.getTime() < pack.saleEndsAt.getTime()
  );
}

/**
 * 主催者が今このパックを買うときの値段。候補は「通常価格」「期間限定価格」「乗り換え価格」で、いちばん安いものを使う。
 * - 期間限定価格：開始日時から終了日時の前まで。終了日時を過ぎたら自動で使わなくなる。
 * - 乗り換え価格：同じシリーズの前の版（版番号が小さいもの）を持っていて、締め切り前のとき。根拠には持っている前の版のうち最も新しいもの。
 * 期間限定価格と乗り換え価格が同じ額なら、乗り換え価格として記録する。
 */
export function quotePrice(pack: EditionPack, owned: OwnedEdition[], now: Date): PriceQuote {
  const sale = saleActive(pack, now) ? { label: pack.saleLabel, priceJpy: pack.salePriceJpy!, endsAt: pack.saleEndsAt! } : null;
  let best: PriceQuote = { listJpy: pack.priceJpy, amountJpy: pack.priceJpy, discount: 'none', from: null, until: null, sale };

  const upgradeOpen = pack.upgradePriceJpy !== null && pack.upgradePriceJpy < pack.priceJpy && !(pack.upgradeUntil && now.getTime() >= pack.upgradeUntil.getTime());
  const prev = upgradeOpen
    ? owned.filter((o) => o.seriesKey === pack.seriesKey && o.packId !== pack.id && o.editionNo < pack.editionNo).sort((a, b) => b.editionNo - a.editionNo)[0]
    : undefined;
  if (prev && pack.upgradePriceJpy! < best.amountJpy) {
    best = {
      ...best,
      amountJpy: pack.upgradePriceJpy!,
      discount: 'upgrade',
      from: { entitlementId: prev.entitlementId, packId: prev.packId, editionLabel: prev.editionLabel },
      until: pack.upgradeUntil,
    };
  }
  if (sale && sale.priceJpy < best.amountJpy) {
    best = { ...best, amountJpy: sale.priceJpy, discount: 'campaign', from: null, until: sale.endsAt };
  }
  return best;
}

/**
 * 期間限定価格の終了日時の表示（日本時間）。ちょうど0時に終わるなら前の日の日付だけ（「3月31日まで」）、
 * それ以外は時刻も出す（「4月1日 12:00まで」）。
 */
export function saleEndText(endsAt: Date | string) {
  const d = new Date(endsAt);
  const jst = new Date(d.getTime() + 9 * 3600_000);
  const midnight = jst.getUTCHours() === 0 && jst.getUTCMinutes() === 0 && jst.getUTCSeconds() === 0;
  const day = (x: Date) => `${x.getUTCMonth() + 1}月${x.getUTCDate()}日`;
  if (midnight) return day(new Date(jst.getTime() - 1));
  return `${day(jst)} ${String(jst.getUTCHours()).padStart(2, '0')}:${String(jst.getUTCMinutes()).padStart(2, '0')}`;
}

/** 「発売記念価格 1,980円（3月31日まで。以降は3,000円）」 */
export function saleText(sale: { label: string; priceJpy: number; endsAt: Date | string }, listJpy: number) {
  const yen = (n: number) => `${n.toLocaleString('ja-JP')}円`;
  return `${sale.label} ${yen(sale.priceJpy)}（${saleEndText(sale.endsAt)}まで。以降は${yen(listJpy)}）`;
}

/** 有料パックは大パック（300問）だけ。価格の既定値は3,000円（税込） */
/**
 * パックのバージョン（2026-10-09）。パックの名前には入れず、パックの中に持つ（edition_label）。
 * 表記は「バージョン 1.0」「バージョン α」。前の「第N版」は N.0 にした
 */
export const DEFAULT_VERSION = '1.0';
export const versionText = (label: string) => `バージョン ${label}`;
/** 「一般常識（バージョン 1.0）」 */
export const packWithVersion = (title: string, label: string) => `${title}（バージョン ${label}）`;
/** 新しいバージョンの名前の初期値（2番目なら 2.0） */
export const nextVersionLabel = (editionNo: number) => `${editionNo}.0`;

/** 公開に要る最低の問題数（2026-10-10 無料・有料とも1問から。システム設定で上げられる）。問題数は入っている問題の数そのもの */
export const PACK_MIN_QUESTIONS = { free: 1, paid: 1 } as const;
export const packMinQuestions = (kind: 'free' | 'paid') => PACK_MIN_QUESTIONS[kind];
export const PAID_PACK_DEFAULT_PRICE_JPY = 3000;

/** 管理画面で新しい版を作るときの乗り換え価格の既定値（通常価格の50%、10円単位で切り捨て） */
export function defaultUpgradePrice(priceJpy: number) {
  return Math.floor(priceJpy / 2 / 10) * 10;
}

/** 購入直前の確認画面に出す文 */
export const BUY_ONCE_NOTE = '買い切り（サービス提供中はずっと使えます）';
export const TERMS_REFUND_NOTE = '購入後の返品・返金はできません（利用規約第9条）';

/**
 * 乗り換え価格の締め切りの表示（日本時間）。締め切りは「その日の終わり＝翌日0時」で持つので、
 * 1ミリ秒前の日付を出す（2027-03-31T15:00Z → 2027年3月31日）。
 */
export function deadlineText(until: Date | string) {
  const t = new Date(new Date(until).getTime() - 1);
  return new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: 'long', day: 'numeric' }).format(t);
}
